import { createApi } from "../../../utils/createApi";
import { AxiosError, AxiosResponse } from "axios";
import { UnitScanPollResponse } from "./onvifScanInterfaces";
import { useOnvifScanStore } from "../onvifScanStore/onvifScanStore";
import { buildUnitScanRows, preselectUnitScanRows } from "./cameraUnitScan";

const POLL_INTERVAL_MS = 3000;
// Same budget as the discovery scan: a site can have dozens of units behind a
// VPN, each probed with several logins.
const MAX_WAIT_MS = 5 * 60 * 1000;

/**
 * Poll GET camera-unit-scan/{task_id}/ until the job resolves, then write the
 * result and the default selection (every camera the scan would change) into
 * the OnvifScan store. Same polling semantics as pollOnvifScan.
 */
export const pollCameraUnitScan = (taskId: string, startTime: number) => {
  const AxiosUI = createApi("");
  const {
    setUnitScanResult,
    setUnitSelectedIds,
    setUnitScanCredentialRefs,
    setScanStatus,
    setErrorMessage,
  } = useOnvifScanStore.getState();

  const finishFailed = (message: string) => {
    setErrorMessage(message);
    setScanStatus("failed");
  };

  if (Date.now() - startTime > MAX_WAIT_MS) {
    finishFailed("Scan timed out. Please try again.");
    return;
  }

  AxiosUI.get(`camera-unit-scan/${taskId}/`).then(
    (response: AxiosResponse<UnitScanPollResponse>) => {
      const data = response.data || ({} as UnitScanPollResponse);

      if (data.status === "success") {
        const result = data.result ?? null;
        setUnitScanResult(result);
        if (result?.credentials) {
          setUnitScanCredentialRefs(result.credentials);
        }
        setUnitSelectedIds(preselectUnitScanRows(buildUnitScanRows(result)));
        setScanStatus("success");
        return;
      }

      if (data.status === "failed") {
        finishFailed(data.detail || data.error || "Camera unit scan failed.");
        return;
      }

      // pending (or unknown) — keep polling
      setTimeout(() => pollCameraUnitScan(taskId, startTime), POLL_INTERVAL_MS);
    },
    (_reason: AxiosError) => {
      // Transient poll error — retry until the max wait elapses.
      setTimeout(() => pollCameraUnitScan(taskId, startTime), POLL_INTERVAL_MS);
    }
  );
};
